/* Hallmark · redesign: resumable invitation task workspace · genre: editorial · macrostructure: Workbench
 * variation: persistent task rail + saved-preview context replace the prior narrow stacked form
 * The server owns task status and section links; the client shows one logical task, saves before
 * navigation, and collapses the persistent rail into an accessible mobile sheet. No new visual
 * tokens: paper, ink, gold, typography, spacing, and motion remain locked to host_ui.css.
 * Hallmark · pre-emit critique: P5 H5 E5 S5 R4 V4 · slop test: pass
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";
import {
  CheckCircle2Icon,
  CircleDashedIcon,
  EyeIcon,
  MenuIcon,
} from "lucide-react";

import { Form, type FormProps } from "@javascript/components/Inputs";
import { InvitationContentEditor, type InvitationContentEditorProps } from "@javascript/components/InvitationContentEditor";
import { SetupProgressRail, type SetupStep } from "@javascript/components/SetupProgressRail";
import { useAppFlash } from "@javascript/flash";
import { Button } from "@javascript/components/ui/button";
import { Progress } from "@javascript/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@javascript/components/ui/sheet";
import { TypographyH1, TypographyMuted, TypographyP } from "@javascript/components/ui/typography";
import { cn } from "@javascript/components/utils";

type JourneyStatus = "needs_details" | "complete" | "optional" | "added" | "needs_attention";

type EditorStep = {
  key: string;
  label: string;
  title: string;
  description: string;
  group: "essentials" | "personal_touches";
  required: boolean;
  status: JourneyStatus;
  href: string;
};

type EditorJourney = {
  activeKey: string;
  overviewPath: string;
  steps: EditorStep[];
};

type InvitationSummary = {
  coupleNames: string;
  themeName: string;
  themePreviewUrl: string;
  readyToPublish: boolean;
  published: boolean;
};

type EditInvitationContent = {
  editInvitationForm: FormProps;
  contentEditor: InvitationContentEditorProps;
  editorJourney: EditorJourney;
  invitationSummary: InvitationSummary;
  venueMapSearch: { available: boolean; apiKey: string | null; mapId: string | null };
  previewInvitationPath: string;
  inSetup: boolean;
  setupSteps?: SetupStep[];
  setupComplete: boolean;
};

const STATUS_LABELS: Record<JourneyStatus, string> = {
  needs_details: "Needs details",
  complete: "Complete",
  optional: "Optional",
  added: "Added",
  needs_attention: "Needs attention",
};

export default function EditInvitation() {
  const {
    editInvitationForm,
    contentEditor,
    editorJourney,
    invitationSummary,
    venueMapSearch,
    previewInvitationPath,
    inSetup,
    setupSteps,
    setupComplete,
  } = useContent<EditInvitationContent>();
  const { form, extras } = editInvitationForm;
  const flash = useAppFlash();
  const [pendingAction, setPendingAction] = React.useState<string | null>(null);
  const serializedActionRef = React.useRef<HTMLInputElement>(null);
  const activeStep = editorJourney.steps.find((step) => step.key === editorJourney.activeKey);
  const activeSection = contentEditor.sections.find((section) => section.key === editorJourney.activeKey);
  const showingOverview = editorJourney.activeKey === "overview" || !activeStep || !activeSection;
  const showingCompletion = showingOverview && setupComplete && (
    flash.success === "Your invitation is ready to preview." || flash.success === "Your invitation is ready."
  );

  React.useEffect(() => {
    const resetSubmitting = () => setPendingAction(null);
    window.addEventListener("superglue:visit-complete", resetSubmitting);

    return () => window.removeEventListener("superglue:visit-complete", resetSubmitting);
  }, []);

  const workspace = (
    <div
      data-invitation-editor-workspace
      className="grid min-w-0 lg:grid-cols-[16rem_minmax(0,1fr)] xl:grid-cols-[16rem_minmax(0,1fr)_17rem]"
    >
      <aside className="hidden border-r bg-muted/35 p-5 lg:block">
        <div className="sticky top-6">
          <JourneyNavigation
            journey={editorJourney}
            formId={showingOverview ? undefined : form.id}
            disabled={pendingAction !== null}
          />
        </div>
      </aside>

      <main className="min-w-0 px-5 py-6 sm:px-8 sm:py-8">
        <MobileJourneyHeader
          journey={editorJourney}
          formId={showingOverview ? undefined : form.id}
          disabled={pendingAction !== null}
        />

        {showingOverview ? (
          <EditorOverview
            journey={editorJourney}
            previewInvitationPath={previewInvitationPath}
            readyToPublish={invitationSummary.readyToPublish}
            showingCompletion={showingCompletion}
          />
        ) : (
          <EditorTask
            step={activeStep}
            section={activeSection}
            contentEditor={contentEditor}
            venueMapSearch={venueMapSearch}
            continueTo={continueTarget(activeStep.key, editorJourney.steps)}
            pendingAction={pendingAction}
            published={invitationSummary.published}
          />
        )}
      </main>

      <SavedPreview
        summary={invitationSummary}
        previewInvitationPath={previewInvitationPath}
      />
    </div>
  );

  return (
    <div className="p-3 sm:p-6">
      <section className="mx-auto w-full max-w-7xl overflow-visible rounded-lg border bg-background shadow-sm">
        <header className="border-b px-5 py-5 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-muted-foreground">Invitation editor</p>
              <p className="truncate font-serif text-xl font-semibold">{invitationSummary.coupleNames}</p>
            </div>
            <Button variant="outline" asChild className="min-h-11 w-fit">
              <a href={previewInvitationPath} target="_blank" rel="noreferrer">
                <EyeIcon aria-hidden="true" />
                Preview saved draft
              </a>
            </Button>
          </div>
          {inSetup && setupSteps && (
            <div className="mt-5 max-w-2xl">
              <SetupProgressRail steps={setupSteps} />
            </div>
          )}
        </header>

        {showingOverview ? (
          workspace
        ) : (
          <Form
            {...form}
            extras={extras}
            data-sg-visit="true"
            data-map-search-available={venueMapSearch.available}
            onSubmit={(event) => {
              const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
              const action = submitter?.name || "continue_to";
              const serializedAction = serializedActionRef.current;

              if (serializedAction) {
                serializedAction.name = action;
                serializedAction.value = submitter?.value || "1";
              }

              // Superglue v2 builds FormData without passing SubmitEvent#submitter.
              // Mirror the successful control into a hidden input before its UJS
              // handler serializes the form, then defer the loading rerender.
              queueMicrotask(() => setPendingAction(action));
            }}
          >
            <input type="hidden" name="editor_section" value={activeStep.key} />
            <input ref={serializedActionRef} type="hidden" data-editor-serialized-action="true" />
            {workspace}
          </Form>
        )}
      </section>
    </div>
  );
}

function JourneyNavigation({
  journey,
  formId,
  disabled,
}: {
  journey: EditorJourney;
  formId?: string;
  disabled: boolean;
}) {
  const essentials = journey.steps.filter((step) => step.group === "essentials");
  const personalTouches = journey.steps.filter((step) => step.group === "personal_touches");

  return (
    <nav aria-label="Invitation sections">
      <JourneyDestination
        label="Overview"
        href={journey.overviewPath}
        active={journey.activeKey === "overview"}
        formId={formId}
        value="overview"
        disabled={disabled}
      />
      <JourneyGroup
        title="Required"
        subtitle="Needed before you publish"
        steps={essentials}
        journey={journey}
        formId={formId}
        disabled={disabled}
      />
      <JourneyGroup
        title="Optional"
        subtitle="Add these any time"
        steps={personalTouches}
        journey={journey}
        formId={formId}
        disabled={disabled}
      />
      {formId && (
        <TypographyMuted className="mt-5 border-t pt-4">
          Moving between sections saves your current work first.
        </TypographyMuted>
      )}
    </nav>
  );
}

function JourneyGroup({
  title,
  subtitle,
  steps,
  journey,
  formId,
  disabled,
}: {
  title: string;
  subtitle: string;
  steps: EditorStep[];
  journey: EditorJourney;
  formId?: string;
  disabled: boolean;
}) {
  if (steps.length === 0) return null;

  return (
    <div className="mt-6">
      <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{title}</p>
      <p className="mb-2 text-xs text-muted-foreground">{subtitle}</p>
      <ul className="space-y-1">
        {steps.map((step) => (
          <li key={step.key}>
            <JourneyDestination
              label={step.label}
              status={step.status}
              href={step.href}
              active={journey.activeKey === step.key}
              formId={formId}
              value={step.key}
              disabled={disabled}
            />
            <p className={cn("px-3 pb-1 text-xs", statusTone(step.status))}>
              {STATUS_LABELS[step.status]}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JourneyDestination({
  label,
  status,
  href,
  active,
  formId,
  value,
  disabled,
}: {
  label: string;
  status?: JourneyStatus;
  href: string;
  active: boolean;
  formId?: string;
  value: string;
  disabled: boolean;
}) {
  const content = (
    <>
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>
      {status && <StatusIcon status={status} />}
    </>
  );
  const classes = cn(
    "flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:bg-accent/80",
    active ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-accent/60",
  );

  if (formId) {
    return (
      <button
        type="submit"
        name="navigate_to"
        value={value}
        form={formId}
        formNoValidate
        disabled={disabled || active}
        aria-current={active ? "page" : undefined}
        className={cn(classes, "disabled:cursor-not-allowed disabled:opacity-60")}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      href={href}
      data-sg-visit="true"
      aria-current={active ? "page" : undefined}
      className={classes}
    >
      {content}
    </a>
  );
}

function MobileJourneyHeader({
  journey,
  formId,
  disabled,
}: {
  journey: EditorJourney;
  formId?: string;
  disabled: boolean;
}) {
  const essentials = journey.steps.filter((step) => step.required);
  const essentialCount = essentials.filter((step) => step.status === "complete").length;

  return (
    <div className="mb-6 border-b pb-5 lg:hidden">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{essentialCount} of {essentials.length} essentials complete</p>
          <Progress
            value={essentials.length === 0 ? 100 : (essentialCount / essentials.length) * 100}
            aria-label="Essential invitation details progress"
            className="mt-2"
          />
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <Button type="button" variant="outline" className="min-h-11 shrink-0">
              <MenuIcon aria-hidden="true" />
              All sections
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Invitation sections</SheetTitle>
              <SheetDescription>Choose what you want to work on next.</SheetDescription>
            </SheetHeader>
            <div className="px-4 pb-6">
              <JourneyNavigation journey={journey} formId={formId} disabled={disabled} />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function EditorOverview({
  journey,
  previewInvitationPath,
  readyToPublish,
  showingCompletion,
}: {
  journey: EditorJourney;
  previewInvitationPath: string;
  readyToPublish: boolean;
  showingCompletion: boolean;
}) {
  const essentials = journey.steps.filter((step) => step.group === "essentials");
  const personalTouches = journey.steps.filter((step) => step.group === "personal_touches");
  const essentialCount = essentials.filter((step) => step.status === "complete").length;
  const nextStep = essentials.find((step) => step.status !== "complete") || personalTouches.find((step) => step.status === "needs_attention");

  return (
    <div className="mx-auto max-w-3xl">
      <TypographyH1 className="text-left">Build your invitation</TypographyH1>
      <TypographyP className="mt-3 max-w-2xl text-muted-foreground">
        Start with the two details every guest needs. Your story, people, photos, and travel plans can be added whenever they are ready.
      </TypographyP>

      {showingCompletion && (
        <div data-setup-complete className="mt-7 border-y border-success/40 bg-success/10 py-4">
          <p className="font-medium text-success">Your invitation is ready to preview.</p>
          <TypographyMuted className="mt-1">The essentials are saved. Optional sections can wait.</TypographyMuted>
        </div>
      )}

      <section className="mt-8" aria-labelledby="essential-progress-heading">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 id="essential-progress-heading" className="font-serif text-2xl font-semibold">Required</h2>
            <TypographyMuted className="mt-1">Names, date, and place — needed before you publish</TypographyMuted>
          </div>
          <p className="shrink-0 text-sm font-medium tabular-nums">{essentialCount} of {essentials.length}</p>
        </div>
        <Progress
          value={essentials.length === 0 ? 100 : (essentialCount / essentials.length) * 100}
          aria-label="Required sections progress"
          className="mt-4"
        />
        <OverviewTaskList steps={essentials} />
      </section>

      <section className="mt-10 border-t pt-8" aria-labelledby="personal-touches-heading">
        <h2 id="personal-touches-heading" className="font-serif text-2xl font-semibold">Optional</h2>
        <TypographyMuted className="mt-1">Add these any time. A section you skip does not appear on your invitation at all.</TypographyMuted>
        <OverviewTaskList steps={personalTouches} />
      </section>

      <div className="mt-8 flex flex-col gap-3 border-t pt-6 sm:flex-row">
        {nextStep ? (
          <Button asChild className="min-h-11">
            <a href={nextStep.href} data-sg-visit="true">Continue setup</a>
          </Button>
        ) : (
          <Button asChild className="min-h-11">
            <a href={previewInvitationPath} target="_blank" rel="noreferrer">Preview invitation</a>
          </Button>
        )}
        {readyToPublish && (
          <Button variant="outline" asChild className="min-h-11">
            <a href="/dashboard" data-sg-visit="true">Back to dashboard</a>
          </Button>
        )}
      </div>
    </div>
  );
}

function OverviewTaskList({ steps }: { steps: EditorStep[] }) {
  return (
    <ul className="mt-4 divide-y border-y">
      {steps.map((step) => (
        <li
          key={step.key}
          className="grid min-h-16 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-1 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
        >
          <StatusIcon status={step.status} />
          <div className="min-w-0">
            <a
              href={step.href}
              data-sg-visit="true"
              className="block w-fit max-w-full truncate whitespace-nowrap font-medium outline-none hover:text-primary focus-visible:ring-[3px] focus-visible:ring-ring/50 active:text-primary/80"
            >
              {step.label}
            </a>
            <p className="text-sm text-muted-foreground">{step.description}</p>
          </div>
          <span className={cn("col-start-2 text-sm sm:col-start-3 sm:row-start-1", statusTone(step.status))}>
            {STATUS_LABELS[step.status]}
          </span>
        </li>
      ))}
    </ul>
  );
}

function EditorTask({
  step,
  section,
  contentEditor,
  venueMapSearch,
  continueTo,
  pendingAction,
  published,
}: {
  step: EditorStep;
  section: InvitationContentEditorProps["sections"][number];
  contentEditor: InvitationContentEditorProps;
  venueMapSearch: EditInvitationContent["venueMapSearch"];
  continueTo: string;
  pendingAction: string | null;
  published: boolean;
}) {
  return (
    <div className="mx-auto max-w-3xl">
      <p className={cn("text-sm font-medium", step.required ? "text-foreground" : "text-muted-foreground")}>
        {step.required ? "Essential" : "Optional — skip it for now if you like"}
      </p>
      <TypographyH1 className="mt-1 text-left">{step.title}</TypographyH1>
      <TypographyP className="mt-3 max-w-2xl text-muted-foreground">{step.description}</TypographyP>

      <div className="mt-8">
        <InvitationContentEditor
          {...contentEditor}
          sections={[ section ]}
          venueMapSearch={venueMapSearch}
          topLevelHeading="sr-only"
        />
      </div>

      <div className="sticky bottom-0 z-10 -mx-5 mt-10 border-t bg-background px-5 py-4 sm:-mx-8 sm:px-8">
        {published && (
          <TypographyMuted className="mb-3">
            This invitation is live, so changes must be complete before saving.
          </TypographyMuted>
        )}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button className="min-h-11" type="submit" name="continue_to" value={continueTo} disabled={pendingAction !== null}>
            {pendingAction === "continue_to" ? "Saving…" : "Save & continue"}
          </Button>
          <Button
            type="submit"
            name="save_for_later"
            value="1"
            formNoValidate={!published}
            variant="outline"
            className="min-h-11"
            disabled={pendingAction !== null}
          >
            {pendingAction === "save_for_later" ? "Saving…" : published ? "Save changes" : "Save for later"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SavedPreview({
  summary,
  previewInvitationPath,
}: {
  summary: InvitationSummary;
  previewInvitationPath: string;
}) {
  return (
    <aside className="hidden border-l p-5 xl:block">
      <div className="sticky top-6">
        <p className="text-sm font-medium">Saved preview</p>
        <TypographyMuted className="mt-1">Preview reflects your last save.</TypographyMuted>
        <img
          src={summary.themePreviewUrl}
          alt={`${summary.themeName} theme preview`}
          width={360}
          height={480}
          className="mt-4 aspect-[3/4] w-full rounded-md border object-cover"
        />
        <p className="mt-4 truncate font-serif text-lg font-semibold">{summary.coupleNames}</p>
        <p className="mt-1 text-sm text-muted-foreground">{summary.themeName} theme</p>
        <div className="mt-4 flex items-center gap-2 border-y py-3 text-sm">
          {summary.readyToPublish ? (
            <CheckCircle2Icon aria-hidden="true" className="size-4 text-success" />
          ) : (
            <CircleDashedIcon aria-hidden="true" className="size-4 text-muted-foreground" />
          )}
          <span>{summary.readyToPublish ? "Ready to publish" : "Details need attention"}</span>
        </div>
        <Button variant="outline" asChild className="mt-4 min-h-11 w-full">
          <a href={previewInvitationPath} target="_blank" rel="noreferrer">
            <EyeIcon aria-hidden="true" />
            Preview saved draft
          </a>
        </Button>
      </div>
    </aside>
  );
}

function StatusIcon({ status }: { status: JourneyStatus }) {
  if (status === "complete" || status === "added") {
    return <CheckCircle2Icon aria-hidden="true" className="size-4 shrink-0 text-success" />;
  }

  return <CircleDashedIcon aria-hidden="true" className={cn("size-4 shrink-0", statusTone(status))} />;
}

function statusTone(status: JourneyStatus): string {
  if (status === "complete" || status === "added") return "text-success";
  if (status === "needs_attention") return "text-destructive";
  return "text-muted-foreground";
}

function continueTarget(activeKey: string, steps: EditorStep[]): string {
  if (activeKey === "couple") return steps.some((step) => step.key === "events") ? "events" : "overview";
  if (activeKey === "events") return "overview";

  const optionalSteps = steps.filter((step) => !step.required);
  const currentIndex = optionalSteps.findIndex((step) => step.key === activeKey);
  return optionalSteps[currentIndex + 1]?.key || "overview";
}
